import { ButtonBase, GridLegacy as Grid, Stack, createFilterOptions, styled } from '@mui/material'
import { PencilSimpleIcon, PlusIcon, XIcon } from '@phosphor-icons/react'
import { Alert, DisplayUserLink, FormTemplate, LoadingSkeleton, Modal, TinyButton, tokens, UserLinkList } from 'ComponentLibrary'
import { joinDefinedStrings } from 'ComponentLibrary/src/common/strings'
import type { UserPresentation } from 'ComponentLibrary/src/common/userPresentations'
import { assembleNameWithEmailFallback, assembleUserSearchText, type User } from 'ComponentLibrary/src/common/users'
import { IconButton, Typography } from 'ComponentLibrary/src/piazza'
import { FormAutocomplete, FormTextInput, type DropdownOption } from 'ComponentLibrary/src/piazza/forms'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import { useEffect, useMemo, useState } from 'react'
import { useFieldArray, useForm, useWatch, type UseFormReturn } from 'react-hook-form'

export type InspectorSchema = {
    name?: string
    role?: string
    company?: string
    user_id?: number
}

export type InspectionSchema = {
    primary_inspector: InspectorSchema
    secondary_inspectors: InspectorSchema[]
}

export type InspectorInputCopy = {
    title: string
    editButton: string
    editButtonAriaLabel: string
    addButton: string
    modalTitle: string
    apply: string
    cancel: string
    duplicateWarning: string
    templateLabel: string
    externalInspector: string
    externalInspectorGroup: string
    nameLabel: string
    roleLabel: string
    companyLabel: string
    appraiserRole: string
    addParticipant: string
    remove: string
}

const DEFAULT_INSPECTOR_INPUT_COPY: InspectorInputCopy = {
    title: 'Besichtiger',
    editButton: 'Bearbeiten',
    editButtonAriaLabel: 'Besichtiger bearbeiten',
    addButton: 'Besichtiger/in hinzufügen',
    modalTitle: 'Besichtiger/innen',
    apply: 'Übernehmen',
    cancel: 'Abbrechen',
    duplicateWarning: 'Dieselbe Person wurde mehrfach als Besichtiger/in ausgewählt. Bitte ändern Sie einen der Einträge.',
    templateLabel: 'Besichtiger/in',
    externalInspector: '► Extern',
    externalInspectorGroup: 'Nicht registrierte Besichtiger/innen',
    nameLabel: 'Name',
    roleLabel: 'Rolle',
    companyLabel: 'Firma',
    appraiserRole: 'Gutachter',
    addParticipant: 'Weitere/n Besichtiger/in hinzufügen',
    remove: 'Entfernen',
}

type InspectorFormValues = { inspection?: InspectionSchema }

type InspectorInputProps<TFieldValues extends InspectorFormValues> = {
    appraisers: User[]
    formProps: UseFormReturn<TFieldValues>
    copy?: Partial<InspectorInputCopy>
    displayMode?: 'compact' | 'expanded'
    isPending?: boolean
    userLinkMaxWidth?: string
}

type InspectorSelection = number | 'external'
type InspectorModalEntry = InspectorSchema & { selection?: InspectorSelection }
type InspectorModalFormValues = { inspectors: InspectorModalEntry[] }

const emptyInspector = (): InspectorModalEntry => ({ name: '', role: '', company: '' })

const hasInspectorData = (inspector: InspectorSchema | undefined): inspector is InspectorSchema =>
    !!inspector && Object.values(inspector).some((value) => value !== undefined && value !== '')

const flattenInspection = (inspection?: InspectionSchema) =>
    [inspection?.primary_inspector, ...(inspection?.secondary_inspectors ?? [])].filter(hasInspectorData)

const groupAppraisersByOrganization = (appraisers: User[]) => {
    const groups = new Map<string, User[]>()

    appraisers.forEach((appraiser) => {
        const organization = appraiser.organization_name ?? ''
        const group = groups.get(organization)
        if (group) {
            group.push(appraiser)
        } else {
            groups.set(organization, [appraiser])
        }
    })

    return [...groups.values()].flat()
}

const toModalEntry = (inspector: InspectorSchema): InspectorModalEntry => ({
    ...inspector,
    selection: inspector.user_id ?? 'external',
})

const getModalDefaultValues = (inspection?: InspectionSchema): InspectorModalFormValues => {
    const inspectors = flattenInspection(inspection).map(toModalEntry)
    return { inspectors: inspectors.length > 0 ? inspectors : [emptyInspector()] }
}

const getInspectorPresentation = (inspector: InspectorSchema, appraisers: User[], index: number): UserPresentation => {
    const user = appraisers.find((appraiser) => appraiser.id === inspector.user_id)
    if (user) {
        return { kind: 'account', key: `inspector:${user.id}:${index}`, displayData: user }
    }

    if (inspector.user_id !== undefined) {
        return { kind: 'unavailable', key: `inspector:${inspector.user_id}:${index}` }
    }

    return {
        kind: 'nonUser',
        key: `inspector:${inspector.user_id ?? 'external'}:${index}`,
        displayData: {
            first_name: joinDefinedStrings([inspector.name, inspector.role], ' - '),
            organization_name: inspector.company,
        },
    }
}

export function InspectorInput<TFieldValues extends InspectorFormValues>({
    appraisers,
    formProps,
    copy,
    displayMode = 'compact',
    isPending = false,
    userLinkMaxWidth,
}: InspectorInputProps<TFieldValues>) {
    const resolvedCopy = { ...DEFAULT_INSPECTOR_INPUT_COPY, ...copy }
    const [isOpen, setIsOpen] = useState(false)
    const { control, setValue } = formProps as unknown as UseFormReturn<InspectorFormValues>
    const inspection = useWatch({ control, name: 'inspection' })
    const inspectors = flattenInspection(inspection)
    const presentations = inspectors.map((inspector, index) => getInspectorPresentation(inspector, appraisers, index))
    const hasInspectors = presentations.length > 0
    const inspectorList = (
        <UserLinkList
            presentations={presentations}
            size="medium"
            maxWidth={userLinkMaxWidth}
            showAll={displayMode === 'expanded'}
            showNonUserChips={displayMode === 'expanded'}
        />
    )

    const updateInspection = (newInspection?: InspectionSchema) => {
        setValue('inspection', newInspection, { shouldDirty: true })
        setIsOpen(false)
    }

    let inspectorControl = inspectorList
    if (!hasInspectors) {
        inspectorControl = <TinyButton label={resolvedCopy.addButton} leftIcon={<PlusIcon />} onClick={() => setIsOpen(true)} />
    } else if (displayMode === 'compact') {
        inspectorControl = (
            <StyledInspectorButton aria-label={resolvedCopy.editButtonAriaLabel} onClick={() => setIsOpen(true)}>
                {inspectorList}
                <PencilSimpleIcon size={tokens.SizingIconS} weight="bold" color={tokens.ColorIconDefault} />
            </StyledInspectorButton>
        )
    }

    return (
        <Stack direction="column" gap={tokens.SizingSpacing150}>
            {displayMode === 'expanded' ? (
                <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                    <Headline variant="headline4" title={resolvedCopy.title} fullWidth={false} />
                    {!isPending && hasInspectors && (
                        <TinyButton
                            label={resolvedCopy.editButton}
                            ariaLabel={resolvedCopy.editButtonAriaLabel}
                            leftIcon={<PencilSimpleIcon />}
                            onClick={() => setIsOpen(true)}
                        />
                    )}
                </Stack>
            ) : (
                <Headline variant="headline3" title={resolvedCopy.title} />
            )}
            {isPending ? <LoadingSkeleton height="56px" variant="rounded" /> : inspectorControl}
            <InspectorInputModal
                open={isOpen}
                onClose={() => setIsOpen(false)}
                onSubmit={updateInspection}
                inspection={inspection}
                appraisers={appraisers}
                copy={resolvedCopy}
            />
        </Stack>
    )
}

type InspectorInputModalProps = {
    open: boolean
    onClose: () => void
    onSubmit: (inspection?: InspectionSchema) => void
    inspection?: InspectionSchema
    appraisers: User[]
    copy: InspectorInputCopy
}

function InspectorInputModal({ open, onClose, onSubmit, inspection, appraisers, copy }: InspectorInputModalProps) {
    const defaultValues = useMemo(() => getModalDefaultValues(inspection), [inspection])
    const [focusIndex, setFocusIndex] = useState<number>()
    const {
        control,
        handleSubmit,
        formState: { isDirty },
        reset,
        setFocus,
    } = useForm<InspectorModalFormValues>({ defaultValues })
    const { fields, append, remove, update } = useFieldArray({ control, name: 'inspectors' })
    const inspectors = useWatch({ control, name: 'inspectors' })

    useEffect(() => {
        if (open) {
            reset(defaultValues)
        }
    }, [defaultValues, open, reset])

    useEffect(() => {
        if (focusIndex !== undefined) {
            setFocus(`inspectors.${focusIndex}.selection`)
            setFocusIndex(undefined)
        }
    }, [fields, focusIndex, setFocus])

    const selectedUserIds = inspectors.flatMap((inspector) => (typeof inspector.selection === 'number' ? [inspector.selection] : []))
    const hasDuplicateUsers = new Set(selectedUserIds).size !== selectedUserIds.length

    const submit = handleSubmit(({ inspectors }) => {
        const persistedInspectors = inspectors
            .map(({ selection, ...inspector }) => {
                if (selection !== 'external') {
                    return inspector
                }
                const { user_id: _userId, ...externalInspector } = inspector
                return externalInspector
            })
            .filter(hasInspectorData)
        const [primaryInspector, ...secondaryInspectors] = persistedInspectors

        onSubmit(primaryInspector ? { primary_inspector: primaryInspector, secondary_inspectors: secondaryInspectors } : undefined)
    })

    const cancel = () => {
        reset(defaultValues)
        onClose()
    }

    return (
        <Modal
            isOpen={open}
            onClose={cancel}
            title={copy.modalTitle}
            footerProps={{
                primaryButton: { text: copy.apply, onClick: submit, disabled: !isDirty || hasDuplicateUsers },
                secondaryButton: { text: copy.cancel, onClick: cancel },
            }}
        >
            <Stack direction="column" gap={tokens.SizingSpacing200} width="100%">
                {hasDuplicateUsers && (
                    <Alert size="small" severity="warning">
                        <Typography variant="textS" color={tokens.ColorTextBold}>
                            {copy.duplicateWarning}
                        </Typography>
                    </Alert>
                )}
                <FormTemplate onSubmit={submit} onCancel={cancel} isFormDirty={isDirty} hideFooter>
                    {fields.map((field, index) => {
                        const inspector = inspectors[index] ?? field
                        const selectedAppraiser = appraisers.find((appraiser) => appraiser.id === inspector.selection)
                        const unavailableAppraiser: User | undefined =
                            typeof inspector.selection === 'number' && !selectedAppraiser
                                ? {
                                      uuid: `unavailable-inspector-${inspector.selection}`,
                                      id: inspector.selection,
                                      first_name: inspector.name,
                                      email: '',
                                      organization_name: inspector.company ?? '',
                                  }
                                : undefined
                        const selectableAppraisers = groupAppraisersByOrganization([
                            ...appraisers,
                            ...(unavailableAppraiser ? [unavailableAppraiser] : []),
                        ])
                        const appraisersById = new Map(selectableAppraisers.map((appraiser) => [appraiser.id, appraiser]))
                        const options: DropdownOption<InspectorSelection>[] = [
                            { label: copy.externalInspector, value: 'external' },
                            ...selectableAppraisers.map((appraiser) => ({
                                label: assembleNameWithEmailFallback(appraiser),
                                value: appraiser.id,
                            })),
                        ]

                        return (
                            <Grid container item xs={12} spacing={tokens.SizingSpacing100} key={field.id}>
                                <Grid item xs={11}>
                                    <Stack direction="column" gap={tokens.SizingSpacing150}>
                                        <FormAutocomplete<InspectorModalFormValues, InspectorSelection>
                                            aria-label={`${copy.templateLabel} ${index + 1}`}
                                            control={control}
                                            filterOptions={(options, state) =>
                                                createFilterOptions<DropdownOption<InspectorSelection>>({
                                                    stringify: (option) => {
                                                        const appraiser =
                                                            typeof option.value === 'number' ? appraisersById.get(option.value) : undefined
                                                        return appraiser ? assembleUserSearchText(appraiser) : option.label
                                                    },
                                                })(
                                                    options.filter(
                                                        (option) =>
                                                            typeof option.value !== 'number' ||
                                                            appraisersById.get(option.value)?.is_active !== false
                                                    ),
                                                    state
                                                )
                                            }
                                            groupBy={(option) =>
                                                option.value === 'external'
                                                    ? copy.externalInspectorGroup
                                                    : (appraisersById.get(option.value)?.organization_name ?? '')
                                            }
                                            label={`${copy.templateLabel} ${index + 1}`}
                                            name={`inspectors.${index}.selection`}
                                            onValueChange={(selection) => {
                                                if (selection === undefined) {
                                                    update(index, emptyInspector())
                                                } else if (selection === 'external') {
                                                    update(index, { ...emptyInspector(), selection })
                                                } else {
                                                    const appraiser = appraisersById.get(selection)
                                                    if (appraiser) {
                                                        update(index, {
                                                            selection,
                                                            user_id: selection,
                                                            name: joinDefinedStrings([appraiser.first_name, appraiser.last_name]),
                                                            role: copy.appraiserRole,
                                                            company: appraiser.organization_name,
                                                        })
                                                    }
                                                }
                                            }}
                                            options={options}
                                            renderOption={(listAttributes, option) => {
                                                const appraiser =
                                                    typeof option.value === 'number' ? appraisersById.get(option.value) : undefined
                                                return (
                                                    <li {...listAttributes} key={listAttributes.key}>
                                                        {appraiser ? (
                                                            <DisplayUserLink user={appraiser} size="medium" showProfileOnHover={false} />
                                                        ) : (
                                                            option.label
                                                        )}
                                                    </li>
                                                )
                                            }}
                                            slotProps={{
                                                paper: {
                                                    sx: {
                                                        '&& .MuiAutocomplete-groupLabel:empty': { display: 'none' },
                                                        '&& .MuiAutocomplete-option': {
                                                            height: 'auto',
                                                            minHeight: tokens.SizingSpacing500,
                                                            padding: `${tokens.SizingSpacing050} ${tokens.SizingSpacing150}`,
                                                        },
                                                    },
                                                },
                                                popper: { sx: { zIndex: '1600 !important' } },
                                            }}
                                        />
                                        <Stack direction="row" gap={tokens.SizingSpacing100}>
                                            <Stack width="100%">
                                                <FormTextInput
                                                    aria-label={`${copy.nameLabel}: ${copy.templateLabel} ${index + 1}`}
                                                    control={control}
                                                    label={copy.nameLabel}
                                                    name={`inspectors.${index}.name`}
                                                />
                                            </Stack>
                                            <Stack width="100%">
                                                <FormTextInput
                                                    aria-label={`${copy.roleLabel}: ${copy.templateLabel} ${index + 1}`}
                                                    control={control}
                                                    label={copy.roleLabel}
                                                    name={`inspectors.${index}.role`}
                                                />
                                            </Stack>
                                            <Stack width="100%">
                                                <FormTextInput
                                                    aria-label={`${copy.companyLabel}: ${copy.templateLabel} ${index + 1}`}
                                                    control={control}
                                                    label={copy.companyLabel}
                                                    name={`inspectors.${index}.company`}
                                                />
                                            </Stack>
                                        </Stack>
                                    </Stack>
                                </Grid>
                                <Grid item xs={1} sx={{ alignSelf: 'center' }}>
                                    <IconButton
                                        icon={<XIcon />}
                                        ariaLabel={`${copy.remove}: ${copy.templateLabel} ${index + 1}`}
                                        tooltip={copy.remove}
                                        size="small"
                                        variant="tertiary"
                                        onClick={() => {
                                            if (fields.length === 1) {
                                                update(index, emptyInspector())
                                            } else {
                                                remove(index)
                                            }
                                            setFocusIndex(Math.max(0, Math.min(index, fields.length - 2)))
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        )
                    })}
                </FormTemplate>
                <TinyButton
                    onClick={() => {
                        setFocusIndex(fields.length)
                        append(emptyInspector())
                    }}
                    label={copy.addParticipant}
                    leftIcon={<PlusIcon />}
                />
            </Stack>
        </Modal>
    )
}

const StyledInspectorButton = styled(ButtonBase)(() => ({
    width: 'fit-content',
    borderRadius: tokens.SizingCornerS,
    padding: tokens.SizingSpacing100,
    gap: tokens.SizingSpacing150,
    '&:hover': {
        backgroundColor: tokens.ColorSurfaceHover,
    },
    '&:active': {
        backgroundColor: tokens.ColorSurfacePressed,
    },
    '& *': {
        cursor: 'pointer',
    },
}))
